import React, { useEffect, useRef, useState } from 'react';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';

interface AsyncSearchSelectProps<T> {
  value: T | null;
  onSelect: (item: T | null) => void;
  fetchOptions: (query: string) => Promise<T[]>;
  getOptionKey: (item: T) => string | number;
  renderOption: (item: T) => React.ReactNode;
  getTriggerLabel: (item: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearable?: boolean;
  onError?: (message: string) => void;
  renderHeader?: (close: () => void) => React.ReactNode;
}

/**
 * Searchable async dropdown styled like the chatbot selector: a trigger button opens a popover with a
 * debounced search input and a scrollable list. Options are fetched whenever the popover opens, so the
 * list never goes stale. Any header content sits above the list so it stays put as results load in.
 */
function AsyncSearchSelect<T>({
  value,
  onSelect,
  fetchOptions,
  getOptionKey,
  renderOption,
  getTriggerLabel,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found',
  clearable = false,
  onError,
  renderHeader
}: Readonly<AsyncSearchSelectProps<T>>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const toggleOpen = (): void => {
    if (!isOpen) {
      setSearch('');
      setOptions([]);
      setLoading(true);
    }
    setIsOpen((prev) => !prev);
  };

  useDebouncedEffect(
    () => {
      if (!isOpen) return;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      fetchOptions(search)
        .then((results) => {
          if (requestId === requestIdRef.current) setOptions(results);
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          onError?.(err.apiError?.message || 'Failed to load options.');
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    },
    [search, isOpen],
    300
  );

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (item: T | null): void => {
    onSelect(item);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className='relative w-full'>
      <button
        type='button'
        onClick={toggleOpen}
        className={
          'border-input focus-visible:border-ring focus-visible:ring-ring/50 ' +
          'flex min-h-9 w-full items-center justify-between gap-2 ' +
          'rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none ' +
          'transition-[color,box-shadow] focus-visible:ring-[3px]'
        }
      >
        <span
          className={`min-w-0 text-left break-words ${value ? '' : 'text-muted-foreground'}`}
        >
          {value ? getTriggerLabel(value) : placeholder}
        </span>
        <ChevronDown className='size-4 shrink-0 opacity-50' />
      </button>

      {isOpen && (
        <div className='absolute top-full left-0 z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg'>
          <div className='flex items-center border-b px-3 py-2'>
            <Search size={16} className='mr-2 flex-shrink-0 text-gray-400' />
            <input
              ref={searchInputRef}
              type='text'
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400'
            />
            {search && (
              <button
                type='button'
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
                className='ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600'
                aria-label='Clear search'
              >
                <X size={16} />
              </button>
            )}
          </div>

          {renderHeader && (
            <div className='border-b'>
              {renderHeader(() => setIsOpen(false))}
            </div>
          )}

          {clearable && value && (
            <button
              type='button'
              onClick={() => handleSelect(null)}
              className='w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50'
            >
              Clear selection
            </button>
          )}

          {loading && (
            <div className='flex items-center justify-center py-4'>
              <Loader2 size={20} className='animate-spin text-gray-400' />
            </div>
          )}

          {!loading && options.length === 0 && (
            <div className='flex items-center justify-center py-4 text-sm text-gray-500'>
              {emptyText}
            </div>
          )}

          {!loading && options.length > 0 && (
            <div className='max-h-60 overflow-y-auto'>
              {options.map((option) => (
                <button
                  key={getOptionKey(option)}
                  type='button'
                  onClick={() => handleSelect(option)}
                  className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'
                >
                  {renderOption(option)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AsyncSearchSelect;
